import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { Card, Page } from "../layout/Page";
import {
  projectApi,
  selectedProjectId,
  selectProject,
  type ProjectSummary,
} from "../workflow";

function formatUpdatedAt(value: string): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return "Update time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(instant);
}

function projectNextStep(project: ProjectSummary): {
  destination: string;
  label: string;
} {
  if (project.read_only || project.saved_optimization_count > 0) {
    return { destination: "/results", label: "Review results" };
  }
  if (project.saved_simulation_count > 0) {
    return { destination: "/optimization", label: "Optimize setup" };
  }
  return { destination: "/network", label: "Build route" };
}

function EmptyWorkspace() {
  return (
    <Card className="dashboard-empty">
      <h3>No projects</h3>
    </Card>
  );
}

function SelectedProject({ project }: { project: ProjectSummary | null }) {
  if (project === null) {
    return (
      <Card title="Selected project">
        <p>No project selected.</p>
        <Link className="button button--primary" to="/projects">
          Select project
        </Link>
      </Card>
    );
  }

  const nextStep = projectNextStep(project);
  return (
    <Card title="Selected project">
      <div className="recent-work__identity">
        <span
          className={
            project.read_only
              ? "status-chip status-chip--readonly"
              : "status-chip"
          }
        >
          {project.read_only ? "Portable" : "Editable"}
        </span>
        <h3>{project.name}</h3>
      </div>
      <p>
        Next step: <strong>{nextStep.label}</strong>
      </p>
      <Link className="button button--primary" to={nextStep.destination}>
        Continue project
      </Link>
    </Card>
  );
}

function RecentWork({
  onSelect,
  projects,
  selectedId,
}: {
  onSelect: (project: ProjectSummary) => void;
  projects: ProjectSummary[];
  selectedId: string | null;
}) {
  const recent = [...projects]
    .sort(
      (left, right) =>
        Date.parse(right.updated_at) - Date.parse(left.updated_at),
    )
    .filter((project) => project.id !== selectedId)
    .slice(0, 4);

  if (recent.length === 0) return <p>No other projects.</p>;

  return (
    <div className="dashboard-content">
      <ul className="recent-work" aria-label="Recent projects">
        {recent.map((project) => {
          const savedCount =
            project.saved_simulation_count + project.saved_optimization_count;
          return (
            <li key={project.id}>
              <div className="recent-work__identity">
                <span
                  className={
                    project.read_only
                      ? "status-chip status-chip--readonly"
                      : "status-chip"
                  }
                >
                  {project.read_only ? "Portable" : "Editable"}
                </span>
                <h3>{project.name}</h3>
                <p>
                  <time dateTime={project.updated_at}>
                    Updated {formatUpdatedAt(project.updated_at)}
                  </time>
                  <span aria-hidden="true"> · </span>
                  {savedCount} saved {savedCount === 1 ? "calculation" : "calculations"}
                </p>
              </div>
              <button
                aria-label={`Select ${project.name}`}
                className="button button--secondary"
                type="button"
                onClick={() => onSelect(project)}
              >
                Select
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function WorkspaceDashboard() {
  const [selectedId, setSelectedId] = useState(() => selectedProjectId());
  const projects = useQuery({
    queryKey: ["flow-wizard", "projects"],
    queryFn: projectApi.list,
    retry: false,
  });

  if (projects.isPending) {
    return (
      <section className="dashboard" aria-label="Project dashboard">
        <Card className="dashboard-loading">
          <div className="loading-line loading-line--wide" />
          <div className="loading-line" />
          <p role="status">Loading projects…</p>
        </Card>
      </section>
    );
  }

  if (projects.isError) {
    return (
      <section className="dashboard" aria-label="Project dashboard">
        <Card className="dashboard-error">
          <div>
            <h3>Projects unavailable</h3>
            <p role="alert">{projects.error.message}</p>
          </div>
          <button
            className="button button--secondary"
            type="button"
            disabled={projects.isFetching}
            onClick={() => void projects.refetch()}
          >
            {projects.isFetching ? "Trying again…" : "Try again"}
          </button>
        </Card>
      </section>
    );
  }

  if (projects.data.length === 0) {
    return (
      <section className="dashboard" aria-label="Project dashboard">
        <EmptyWorkspace />
      </section>
    );
  }

  const selected =
    projects.data.find((project) => project.id === selectedId) ?? null;

  return (
    <div className="dashboard dashboard-content">
      <SelectedProject project={selected} />
      <section aria-labelledby="recent-projects-heading">
        <div className="section-heading">
          <h2 id="recent-projects-heading">Recent projects</h2>
          <Link to="/projects">All projects</Link>
        </div>
        <RecentWork
          onSelect={(project) => {
            selectProject(project.id);
            setSelectedId(project.id);
          }}
          projects={projects.data}
          selectedId={selected?.id ?? null}
        />
      </section>
    </div>
  );
}

export function DashboardPage() {
  return (
    <Page
      title="Dashboard"
      actions={
        <div className="button-row">
          <Link className="button button--secondary" to="/projects">
            Start a project
          </Link>
        </div>
      }
    >
      <WorkspaceDashboard />
    </Page>
  );
}
